/**
 * PatientManageInformantsForm
 * ============================
 *
 * Patient-facing modal for managing their own informants.  This component
 * is a thin wrapper around InformantManagementContent — it provides the
 * Radix AlertDialog shell and passes patient-specific configuration.
 *
 * Patient-specific behaviour:
 *   - Derives patientId from the currently authenticated Firebase user
 *   - Uses PatientSelfDataContext for refreshPatientData
 *   - Uses GlobalStateContext for the user's organisation (to resolve
 *     patient-facing terminology — "Informant" vs "Key Contact")
 *   - Does NOT show the "Resend welcome email" action (only providers can)
 *
 * All informant management logic (data fetching, mutations, screen routing,
 * validation) is handled by InformantManagementContent and the
 * useInformantManagement hook it consumes.
 */

import React, { useContext, useMemo } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { XIcon } from '@heroicons/react/outline';
import { auth } from '../../../../firebase';
import { PatientSelfDataContext } from '../../../../context/PatientSelfDataContext';
import { GlobalStateContext } from '../../../../context/GlobalStateContext';
import { getPatientFacingTerminology } from '../../../../utils/terminology';
import { InformantManagementContent } from '../../../../components/informantManagement';

/**
 * @param {Object}   props
 * @param {boolean}  props.isOpen           – Whether the modal is visible
 * @param {Function} props.onClose          – Called when the modal should close
 * @param {Object}   props.patientDataModel – Full patient document from Firestore
 */
function PatientManageInformantsForm({
  isOpen,
  onClose,
  patientDataModel: patientDataModelProp,
  refreshPatientData: refreshPatientDataProp,
}) {
  const selfContext = useContext(PatientSelfDataContext);
  const refreshPatientData = refreshPatientDataProp || selfContext?.refreshPatientData;
  const patientDataModel = patientDataModelProp || selfContext?.patientDataModel;
  const { userData } = useContext(GlobalStateContext);

  // Resolve organisation-specific terminology (e.g. SLaM uses "Key Contact")
  const baseTerms = useMemo(
    () => getPatientFacingTerminology(userData?.organisation),
    [userData?.organisation]
  );

  // Build the full terminology object expected by InformantManagementContent
  const terminology = useMemo(() => ({
    singular: baseTerms.singular,
    plural: baseTerms.plural,
    lowerSingular: baseTerms.lowerSingular,
    lowerPlural: baseTerms.lowerPlural,
  }), [baseTerms]);

  // The patient's own UID is the patientId for all Cloud Function calls
  const patientId = auth.currentUser?.uid;

  return (
    <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-4 md:p-6 rounded-lg shadow-lg max-w-lg w-[calc(100vw-1rem)] max-h-[95vh] overflow-y-auto z-50 transition-all duration-300 ease-in-out">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 z-10"
            aria-label="Close dialog"
          >
            <XIcon className="w-5 h-5" aria-hidden="true" />
          </button>
          
          <AlertDialog.Title className="text-lg font-bold mb-3 pr-6">
            Manage {terminology.plural}
          </AlertDialog.Title>
          
          <VisuallyHidden.Root>
            <AlertDialog.Description>
              View and manage your {terminology.lowerPlural}, their progress, and assignments
            </AlertDialog.Description>
          </VisuallyHidden.Root>
          
          {patientId ? (
            <InformantManagementContent
              patientId={patientId}
              patientDataModel={patientDataModel}
              refreshPatientData={refreshPatientData}
              onClose={onClose}
              callerType="patient"
              terminology={terminology}
              showResendWelcome={false}
            />
          ) : (
            <div className="py-8 text-center">
              <p className="text-sm text-gray-500">
                Unable to load — please sign in and try again.
              </p>
            </div>
          )}
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export default PatientManageInformantsForm;
